<template>
<v-sheet border rounded  style="min-width:95vw; max-width:95vw">
    <v-data-table
      :headers="headers"
      :items="items"
      item-value="eVoucherId"
      density="compact"
      role="table"
      aria-label="eVoucher reference management data"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>
            <v-icon color="medium-emphasis" icon="mdi-certificate-outline" start></v-icon>
            RISE EVoucher Reference Report
          </v-toolbar-title>
          <v-btn class="ma-2" variant="outlined" prepend-icon="mdi-download" @click="downloadReferenceFile" aria-label="Download Report">
            Reference Report
          </v-btn>
          <v-btn class="ma-2" variant="outlined" @click="downloadOverviewFile" prepend-icon="mdi-download">
            Overview Report
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn class="ma-2" variant="outlined" @click="showBatchNumberInput = true" prepend-icon="mdi-check-all">
            Mark as Utilised
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:item.title="{ value }">
        <v-chip :text="value" border="thin opacity-25" prepend-icon="mdi-book" label>
          <template v-slot:prepend>
            <v-icon color="medium-emphasis"></v-icon>
          </template>
        </v-chip>
      </template>

    </v-data-table>
  </v-sheet>

      <v-dialog v-model="showBatchNumberInput" width="650" scrollable>
        <v-card>
            <v-toolbar color="#0d2155">
                <v-toolbar-title>Batch Number Input</v-toolbar-title>
            </v-toolbar>
            <v-card-text class="pa-4">
                <p class="my-2 general-text">
                    Please enter the batch number for the e-voucher status changes:
                </p>
                <v-text-field v-model="batchNumber" variant="outlined">
                    <template v-slot:label>                                                            	
                        E-voucher reference Batch Number         
                      </template>
                </v-text-field>
            </v-card-text>
            <v-card-actions class="border-t-thin">
                <v-btn @click="showBatchNumberInput = false" width="100">Close</v-btn>
                <v-spacer></v-spacer>
                <v-btn :disabled="!inputDataVerified" width="100" @click="markEVoucherReferenceAsUtilised" color="#055699" variant="elevated">Confirm</v-btn>
            </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showSuccess" width="650" scrollable>
        <v-card class="bg-blue-darken-4">
            <v-card-text class="pa-4">
                <p class="my-2 general-text">
                    Success! Emails sent successfully!
                </p>
            </v-card-text>
            <v-card-actions class="border-t-thin">
                <v-btn @click="showSuccess = false" width="100"><LanguagePlaceholder default-text="Close" /></v-btn>
            </v-card-actions>
        </v-card>
      </v-dialog>

</template>

<script setup>
  import { onMounted, ref, shallowRef, computed } from 'vue'
  import { useSystemStore } from '@/stores/system'
  import { useExcelStore } from '@/stores/excel'

  const systemStore = useSystemStore()
  const excelStore = useExcelStore()
  
  const batchNumber = ref('')
  const showBatchNumberInput = ref(false)
  const showSuccess = ref(false)

  const headers = ref([
    { title: 'Quarter Earned', key: 'quarterEarned', align: 'start' },
    { title: 'Quarter Claimed', key: 'quarterClaimed', align: 'start' },
    { title: 'Verified EVoucher Code', key: 'verifiedEVoucherCode', align: 'start' },
    { title: 'EVoucher Status', key: 'eVoucherStatus', align: 'start' },
    { title: 'EVoucher Expiry Date', key: 'eVoucherExpiryDate', align: 'start' },
    { title: 'Partner Country', key: 'partnerCountry', align: 'start' },
    { title: 'Partner Id', key: 'partnerId', align: 'start' },
    { title: 'Partner Name', key: 'partnerName', align: 'start' },
    { title: 'Distributor Country', key: 'distributorCountry', align: 'start' },
    { title: 'Distributor Id', key: 'distributorId', align: 'start' },
    { title: 'Distributor Name', key: 'distributorName', align: 'start' },
    { title: 'CSG USD Revenue', key: 'csgusdRevenue', align: 'end' },
    { title: 'ISG USD Revenue', key: 'isgusdRevenue', align: 'end' },
    { title: 'CSG USD Claimed', key: 'csgusdClaimed', align: 'end' },
    { title: 'ISG USD Claimed', key: 'isgusdClaimed', align: 'end' },
    { title: 'Local Currency Code', key: 'localCurrencyCode', align: 'start' },
    { title: 'Claimed USD', key: 'claimedUSD', align: 'end' },
    { title: 'Claimed Local Currency', key: 'claimedLocalCurrency', align: 'end' }
  ])

  const items = ref([])

  const inputDataVerified = computed(() => {

    var inputBatchNumber = batchNumber.value != null ? batchNumber.value : ''

    if (inputBatchNumber != '') {
      return true
    } else {
      return false
    }
  })

  const downloadReferenceFile = () => {
    if (items.value.length === 0) {
        return
    }

    excelStore.generateInMemoryExcelDocumentV2(items.value, 'EvoucherReferenceReport').catch(error => {
        console.log('Generate In Memory Excel Document - EvoucherReferenceDocument', error)
    })
  }

  const downloadOverviewFile = () => {
    if (items.value.length === 0) {
        return
    }

    excelStore.generateInMemoryExcelCustomDocument(items.value, 'EvoucherOverviewReport').catch(error => {
        console.log('Generate In Memory Excel Custom Document', error)
    })
  }

  const markEVoucherReferenceAsUtilised = () => {
    if (items.value.length === 0) {
        systemStore.showMessage('There are no records to submit.', {color:'warning'})
        return
    }

    systemStore.markEVoucherReferenceAsUtilised(items.value, batchNumber.value).then(result => {
      if (result.success === true) {
        showBatchNumberInput.value = false
        showSuccess.value = true
      } else {
        systemStore.showMessage('There was an error processing the request, please contact support.', {color:'error'})
      }
    }).catch(error => {
        console.log('Mark EVoucher Reference As Utilised', error)
    })
  }

  onMounted(() => {
    systemStore.getEVoucherReferenceData().then(result => {
      items.value = result.eVoucherReferences
    }).catch(error => {
      console.log('Get EVoucher Reference Report', error)
    })
  })
  
</script>

<style scoped>

</style>